import { ClipboardCheck, MapPin, SlidersHorizontal } from 'lucide-react';
import { HStack, Text } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { StatesSchema, type States } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { EMPLOYER_STATUSES, type EmployerStatus } from '@duke-hq/shared-constants/employer';
import { employerStatusShortLabel } from './employerStatusDisplay';
import { getStateLabels } from '../../constants/student';
import { useSearchParamMultiFilter } from '@duke-hq/filters/hooks/useSearchParamMultiFilter';

export type EmployerFilterValue = {
  statuses: EmployerStatus[];
  states: States[];
};

export function useEmployerFilters(): EmployerFilterValue {
  const { values: statuses } = useSearchParamMultiFilter<EmployerStatus>(
    'status',
    EMPLOYER_STATUSES,
  );
  const { values: states } = useSearchParamMultiFilter<States>('location', StatesSchema.options);
  return { statuses, states };
}

export function EmployerFilters(): React.JSX.Element {
  const { values: statuses, setValues: setStatuses } = useSearchParamMultiFilter<EmployerStatus>(
    'status',
    EMPLOYER_STATUSES,
  );
  const { values: states, setValues: setStates } = useSearchParamMultiFilter<States>(
    'location',
    StatesSchema.options,
  );

  const statusItems = EMPLOYER_STATUSES.flatMap((status) =>
    status === 'AT_CAPACITY'
      ? []
      : [{ id: status, value: status, label: employerStatusShortLabel(status) }],
  );
  const stateLabels = getStateLabels();
  const locationItems = StatesSchema.options.map((state) => ({
    id: state,
    value: state,
    label: stateLabels[state],
  }));

  return (
    <HStack gap={2}>
      <HStack gap={1} color="fg.subtle" pr={1}>
        <SlidersHorizontal size={iconSize.md} />
        <Text fontSize="sm" fontWeight="medium">
          {t`Filters`}
        </Text>
      </HStack>
      <MultipleChoiceFilter
        items={statusItems}
        label={t`Status`}
        name="status"
        onFilterChange={(filters) => setStatuses(filters.status ?? [])}
        onlyUpdateOnChange
        showSearch={false}
        value={statuses.join(',')}
        variant="ghost"
        leftIcon={<ClipboardCheck size={iconSize.md} />}
        rightIcon={undefined}
      />
      <MultipleChoiceFilter
        items={locationItems}
        label={t`Location`}
        name="location"
        onFilterChange={(filters) => setStates(filters.location ?? [])}
        onlyUpdateOnChange
        value={states.join(',')}
        variant="ghost"
        leftIcon={<MapPin size={iconSize.md} />}
        rightIcon={undefined}
      />
    </HStack>
  );
}
